import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EngineController, type ScopeCanvases } from "./engine";
import { PARAMETER_BY_ID } from "./model/catalog";
import { FACTORY_PRESETS } from "./model/defaults";
import { loadStoredPatches, MAX_PATCH_BYTES, parsePatchText, PATCH_STORAGE_KEY, serializePatch } from "./model/patch";
import type { AudioTelemetry, CRTVarPatch, EngineStatus, MidiMapping, PadId, ParameterId, RuntimeState, SourceKind, WorkspaceTab } from "./model/types";
import { appStore, shallowEqual, useAppStore } from "./state/store";
import { Meter, SelectField, StatusLamp, Toggle } from "./components/Controls";
import { Piano } from "./components/Piano";
import { PerformancePads } from "./components/PerformancePads";
import { CompositePanel, ModulationPanel, ScopesPanel, SequencerPanel, SynthPanel, VideoPanel } from "./components/Panels";

const KEY_NOTES: Record<string, number> = { a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11, k: 12 };
const TABS: Array<[WorkspaceTab, string, string]> = [
  ["perform", "PERFORM", "01"], ["video", "VIDEO", "02"], ["synth", "SYNTH", "03"], ["modulation", "MODULATION", "04"],
  ["scopes", "SCOPES", "05"], ["composite", "COMPOSITE LAB", "06"],
];

function isEditable(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.matches("input, select, textarea, [contenteditable=true]") || Boolean(target.closest("[role=dialog]")));
}

function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatClock(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
}

function MasterMeter() {
  const telemetry = useAppStore((value) => value.runtime.telemetry);
  return <Meter label="MASTER" value={telemetry.rms} peak={telemetry.peak} />;
}

function FrameRate() {
  const fps = useAppStore((value) => value.runtime.fps);
  return <>{fps.toFixed(1)} FPS</>;
}

function ScopesWorkspace({ onCanvases }: { onCanvases: (canvases: ScopeCanvases) => void }) {
  const runtime = useAppStore((value) => value.runtime);
  return <ScopesPanel runtime={runtime} onCanvases={onCanvases} />;
}

function CompositeWorkspace({ patch, onPatch }: { patch: CRTVarPatch; onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void }) {
  const runtime = useAppStore((value) => value.runtime);
  return <CompositePanel patch={patch} runtime={runtime} onPatch={onPatch} />;
}

function MidiLearnDialog({ target, onCancel }: { target: ParameterId; onCancel: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background = Array.from(document.querySelectorAll<HTMLElement>(".topbar, #workspace, .footer-strip"));
    background.forEach((element) => { element.inert = true; });
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onCancel(); }
      if (event.key === "Tab") { event.preventDefault(); cancelRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      background.forEach((element) => { element.inert = false; });
      previousFocus?.focus();
    };
  }, [onCancel]);

  return (
    <div className="modal-backdrop" role="presentation">
      <section role="dialog" aria-modal="true" aria-labelledby="midi-learn-title" className="modal">
        <span className="eyebrow">MIDI LEARN</span><h2 id="midi-learn-title">Move a controller</h2>
        <p>Waiting for a CC message to map to {PARAMETER_BY_ID.get(target)?.label}.</p>
        <button ref={cancelRef} type="button" onClick={onCancel}>CANCEL</button>
      </section>
    </div>
  );
}

function PresetBrowser({ factory, users, onLoad, onDuplicate, onRename, onDelete }: {
  factory: readonly CRTVarPatch[];
  users: readonly CRTVarPatch[];
  onLoad: (patch: CRTVarPatch) => void;
  onDuplicate: (patch: CRTVarPatch) => void;
  onRename: (index: number) => void;
  onDelete: (index: number) => void;
}) {
  const [query, setQuery] = useState("");
  const matches = (patch: CRTVarPatch) => patch.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
  const factoryMatches = factory.filter(matches);
  const userMatches = users.map((patch, index) => ({ patch, index })).filter(({ patch }) => matches(patch));
  return (
    <div className="preset-browser">
      <label className="name-field"><span>Find presets</span><input type="search" value={query} placeholder="Search factory and local patches" onChange={(event) => setQuery(event.currentTarget.value)} /></label>
      <div className="preset-browser__group"><span>FACTORY · {factoryMatches.length}</span>{factoryMatches.map((item) => <div className="preset-row" key={`factory-${item.name}`}><strong>{item.name}</strong><button type="button" onClick={() => onLoad(item)}>LOAD</button><button type="button" onClick={() => onDuplicate(item)}>DUPLICATE</button></div>)}</div>
      <div className="preset-browser__group"><span>THIS BROWSER · {userMatches.length}</span>{userMatches.length === 0 ? <small>No matching local patches.</small> : userMatches.map(({ patch: item, index }) => <div className="preset-row" key={`user-${index}-${item.name}`}><strong>{item.name}</strong><button type="button" onClick={() => onLoad(item)}>LOAD</button><button type="button" onClick={() => onDuplicate(item)}>DUPLICATE</button><button type="button" onClick={() => onRename(index)}>RENAME</button><button type="button" onClick={() => onDelete(index)}>DELETE</button></div>)}</div>
    </div>
  );
}

function copyName(name: string, presets: readonly CRTVarPatch[]) {
  const names = new Set(presets.map((item) => item.name.toLocaleLowerCase()));
  const base = `${name} copy`.slice(0, 80);
  if (!names.has(base.toLocaleLowerCase())) return base;
  for (let suffix = 2; suffix < 10_000; suffix += 1) {
    const candidate = `${base.slice(0, 75)} ${suffix}`;
    if (!names.has(candidate.toLocaleLowerCase())) return candidate;
  }
  return `Patch ${Date.now()}`;
}

export function App() {
  const patch = useAppStore((value) => value.patch);
  const runtime = useAppStore((value) => ({
    engineStatus: value.runtime.engineStatus,
    engineMessage: value.runtime.engineMessage,
    activeTab: value.runtime.activeTab,
    bypass: value.runtime.bypass,
    safeOutput: value.runtime.safeOutput,
    recording: value.runtime.recording,
    recordingSeconds: value.runtime.recordingSeconds,
    sourceStatus: value.runtime.sourceStatus,
    sourceMessage: value.runtime.sourceMessage,
    activePads: value.runtime.activePads,
    activeNotes: value.runtime.activeNotes,
    compositeLocked: value.runtime.compositeLocked,
    patchDirty: value.runtime.patchDirty,
    midiStatus: value.runtime.midiStatus,
  }), shallowEqual);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<EngineController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const patchInputRef = useRef<HTMLInputElement>(null);
  const sourceRequestRef = useRef(0);
  const [userPresets, setUserPresets] = useState<CRTVarPatch[]>(() => loadStoredPatches());
  const [notice, setNotice] = useState("Ready. Start the engine when you are.");
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [midiLearnTarget, setMidiLearnTarget] = useState<ParameterId | null>(null);
  const [selectedMidiTarget, setSelectedMidiTarget] = useState<ParameterId>("video.horizontalJitter");

  useEffect(() => {
    if (!canvasRef.current) return;
    let controller: EngineController;
    try {
      controller = new EngineController(canvasRef.current, {
        onStatus: (engineStatus: EngineStatus, engineMessage: string) => appStore.setRuntime({ engineStatus, engineMessage }),
        onTelemetry: (telemetry: AudioTelemetry) => appStore.setRuntime({ telemetry }),
        onFps: (fps: number) => appStore.setRuntime({ fps }),
        onSourceStatus: (sourceStatus: RuntimeState["sourceStatus"], sourceMessage: string) => appStore.setRuntime({ sourceStatus, sourceMessage }),
        onRecording: (recording: boolean, recordingSeconds: number, blob?: Blob) => {
          appStore.setRuntime({ recording, recordingSeconds });
          if (blob) setRecordingBlob(blob);
        },
        onRecordingWarning: (bytes) => setNotice(`Recording has reached ${(bytes / 1024 / 1024).toFixed(0)} MiB and will stop automatically at 1 GiB.`),
        onMidiStatus: (midiStatus: RuntimeState["midiStatus"]) => appStore.setRuntime({ midiStatus }),
        onMidiLearn: () => setMidiLearnTarget(null),
        onMidiMappingsChange: (mappings: readonly MidiMapping[]) => appStore.updatePatch((next) => ({
          ...next,
          midiMappings: [
            ...next.midiMappings.filter((current) => !mappings.some((mapping) => mapping.target === current.target)),
            ...mappings,
          ],
        })),
      });
    } catch (error) {
      appStore.setRuntime({ engineStatus: "error", engineMessage: error instanceof Error ? error.message : "WebGL2 could not be initialized" });
      return;
    }
    engineRef.current = controller;
    return () => { void controller.dispose(); engineRef.current = null; };
    // The engine lifecycle belongs to the canvas, not patch changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void engineRef.current?.applyPatch(patch); }, [patch]);
  useEffect(() => { engineRef.current?.setBypass(runtime.bypass); }, [runtime.bypass]);
  useEffect(() => { engineRef.current?.setSafeOutput(runtime.safeOutput); }, [runtime.safeOutput]);

  const releaseAll = useCallback(() => {
    const snapshot = appStore.getSnapshot();
    for (const owner of Object.keys(snapshot.runtime.activeNotes)) {
      engineRef.current?.noteOff(owner);
      appStore.noteOff(owner);
    }
    for (const [pad, active] of Object.entries(snapshot.runtime.activePads)) {
      if (active && pad !== "panic") engineRef.current?.setPad(pad as PadId, false);
    }
    appStore.setRuntime({ activePads: {} });
  }, []);

  const noteOn = useCallback((owner: string, note: number, velocity = 0.82) => {
    if (appStore.getSnapshot().runtime.engineStatus !== "running") return;
    engineRef.current?.noteOn(owner, note, velocity);
    appStore.noteOn(owner, note);
  }, []);
  const noteOff = useCallback((owner: string) => { engineRef.current?.noteOff(owner); appStore.noteOff(owner); }, []);

  const panic = useCallback(() => {
    releaseAll();
    engineRef.current?.setPad("panic", true);
    appStore.panic();
    if (appStore.getSnapshot().patch.sequencer.enabled) {
      appStore.updatePatch((next) => ({ ...next, sequencer: { ...next.sequencer, enabled: false } }));
    }
    setNotice("Panic latched: clean video and muted audio. Press Resume when safe.");
  }, [releaseAll]);

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey || isEditable(event.target)) return;
      const key = event.key.toLowerCase();
      if (key in KEY_NOTES && patch.keyboard.capture) {
        event.preventDefault();
        const note = (patch.keyboard.octave + 1) * 12 + KEY_NOTES[key];
        noteOn(`key-${key}`, note);
      } else if (key === "z" || key === "x") {
        event.preventDefault();
        appStore.updatePatch((next) => ({ ...next, keyboard: { ...next.keyboard, octave: Math.max(0, Math.min(7, next.keyboard.octave + (key === "z" ? -1 : 1))) } }));
      } else if (key === "b") appStore.setRuntime({ bypass: !appStore.getSnapshot().runtime.bypass });
      else if (key === "escape") panic();
      else if (key === "r" && runtime.engineStatus === "running") void toggleRecording();
      else if (key === "?") setNotice("Keys: A–K notes · Z/X octave · B bypass · R record · Esc panic");
    };
    const up = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key in KEY_NOTES) noteOff(`key-${key}`);
    };
    const visibility = () => { if (document.hidden) releaseAll(); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", releaseAll);
    document.addEventListener("visibilitychange", visibility);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("blur", releaseAll); document.removeEventListener("visibilitychange", visibility); };
  }, [noteOff, noteOn, panic, patch.keyboard.capture, patch.keyboard.octave, releaseAll, runtime.engineStatus]);

  const startEngine = async () => {
    try {
      if (!engineRef.current) throw new Error("This browser could not create the WebGL2 video engine");
      if (runtime.engineStatus === "panic") {
        await engineRef.current.resumeFromPanic();
        appStore.setRuntime({ bypass: false, engineStatus: "running", engineMessage: "Audio + video synchronized", activePads: { panic: false } });
      } else await engineRef.current.start();
      setNotice("Engine running. Play the keys or disturb the signal.");
    } catch (error) {
      appStore.setRuntime({ engineStatus: "error", engineMessage: error instanceof Error ? error.message : "Engine could not start" });
    }
  };

  const changeSource = async (kind: SourceKind) => {
    const request = ++sourceRequestRef.current;
    if (kind === "image" || kind === "video") {
      if (!fileInputRef.current) return;
      fileInputRef.current.accept = kind === "image" ? "image/*" : "video/*";
      fileInputRef.current.dataset.kind = kind;
      fileInputRef.current.dataset.request = String(request);
      fileInputRef.current.click();
      return;
    }
    try {
      if (!engineRef.current) throw new Error("Video engine unavailable");
      await engineRef.current.setSource(kind);
      if (request !== sourceRequestRef.current) return;
      appStore.updatePatch((next) => ({ ...next, source: { kind, fit: next.source.fit } }));
    } catch (error) {
      if (request === sourceRequestRef.current) setNotice(error instanceof Error ? error.message : "Source unavailable");
    }
  };

  const loadLocalSource = async (file: File) => {
    const kind = fileInputRef.current?.dataset.kind as "image" | "video";
    const request = Number(fileInputRef.current?.dataset.request) || ++sourceRequestRef.current;
    try {
      if (!engineRef.current) throw new Error("Video engine unavailable");
      await engineRef.current.setSource(kind, file);
      if (request !== sourceRequestRef.current) return;
      appStore.updatePatch((next) => ({ ...next, source: { kind, fit: next.source.fit, displayName: file.name } }));
      setNotice(`${file.name} stays on this device.`);
    } catch (error) {
      if (request === sourceRequestRef.current) setNotice(error instanceof Error ? error.message : "Media could not be decoded");
    }
  };

  const handlePad = (pad: PadId, pressed: boolean, mode: "gate" | "shot" | "panic") => {
    if (mode === "panic") { panic(); return; }
    engineRef.current?.setPad(pad, pressed);
    appStore.setPad(pad, pressed);
    if (mode === "shot" && pressed) window.setTimeout(() => { engineRef.current?.setPad(pad, false); appStore.setPad(pad, false); }, pad === "syncKick" ? 120 : 180);
  };

  const toggleRecording = async () => {
    try {
      if (appStore.getSnapshot().runtime.recording) {
        const blob = await engineRef.current?.stopRecording();
        if (blob) setRecordingBlob(blob);
      } else {
        setRecordingBlob(null);
        await engineRef.current?.startRecording();
      }
    } catch (error) { setNotice(error instanceof Error ? error.message : "Recording is not supported here"); }
  };

  const savePreset = () => {
    const copy = structuredClone(patch);
    const existing = userPresets.findIndex((item) => item.name === copy.name);
    const next = existing >= 0 ? userPresets.map((item, index) => index === existing ? copy : item) : [...userPresets, copy];
    setUserPresets(next);
    try { localStorage.setItem(PATCH_STORAGE_KEY, JSON.stringify(next)); appStore.setRuntime({ patchDirty: false }); setNotice(`Saved “${copy.name}” locally.`); }
    catch { setNotice("Browser storage is unavailable; use Export instead."); }
  };

  const duplicatePreset = (source: CRTVarPatch) => {
    const copy = structuredClone(source);
    copy.name = copyName(source.name, userPresets);
    const next = [...userPresets, copy];
    setUserPresets(next);
    try { localStorage.setItem(PATCH_STORAGE_KEY, JSON.stringify(next)); setNotice(`Duplicated “${source.name}” as “${copy.name}”.`); }
    catch { setNotice("Browser storage is unavailable; the duplicate lasts only for this session."); }
  };

  const renamePreset = (index: number) => {
    const current = userPresets[index];
    if (!current) return;
    const requested = window.prompt("Rename local patch", current.name)?.trim();
    if (!requested) return;
    const next = userPresets.map((item, itemIndex) => itemIndex === index ? { ...item, name: requested.slice(0, 80) } : item);
    setUserPresets(next);
    try { localStorage.setItem(PATCH_STORAGE_KEY, JSON.stringify(next)); setNotice(`Renamed local patch to “${requested.slice(0, 80)}”.`); }
    catch { setNotice("Browser storage is unavailable; the rename lasts only for this session."); }
  };

  const deletePreset = (index: number) => {
    const current = userPresets[index];
    if (!current || !window.confirm(`Delete the local patch “${current.name}”?`)) return;
    const next = userPresets.filter((_, itemIndex) => itemIndex !== index);
    setUserPresets(next);
    try { localStorage.setItem(PATCH_STORAGE_KEY, JSON.stringify(next)); setNotice(`Deleted “${current.name}”.`); }
    catch { setNotice("Browser storage is unavailable; the deletion lasts only for this session."); }
  };

  const loadPreset = (selected: CRTVarPatch) => {
    if (runtime.patchDirty && !window.confirm("Discard unsaved patch changes?")) return;
    appStore.replacePatch(structuredClone(selected), false);
  };

  const selectPreset = (key: string) => {
    const [group, rawIndex] = key.split(":");
    const index = Number(rawIndex);
    const selected = group === "factory" ? FACTORY_PRESETS[index] : group === "user" ? userPresets[index] : undefined;
    if (!selected) return;
    loadPreset(selected);
  };

  const importPatch = async (file: File) => {
    try {
      if (file.size > MAX_PATCH_BYTES) throw new Error("Patch exceeds the 256 KiB import limit.");
      appStore.replacePatch(parsePatchText(await file.text()), false); setNotice(`Imported ${file.name}.`);
    }
    catch (error) { setNotice(error instanceof Error ? error.message : "Patch import failed"); }
  };

  const activeNotes = useMemo(() => Object.values(runtime.activeNotes), [runtime.activeNotes]);
  const setParameter = useCallback((id: ParameterId, value: number) => {
    appStore.setParameter(id, value);
    engineRef.current?.setParameter(id, value);
  }, []);
  const enableMidi = useCallback(async () => {
    try {
      if (!engineRef.current) throw new Error("Engine unavailable");
      await engineRef.current.enableMidi();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "MIDI access was not granted");
    }
  }, []);
  const beginMidiLearn = useCallback(async () => {
    try {
      if (!engineRef.current) throw new Error("Engine unavailable");
      await engineRef.current.enableMidi();
      engineRef.current.beginMidiLearn(selectedMidiTarget);
      setMidiLearnTarget(selectedMidiTarget);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "MIDI access was not granted");
    }
  }, [selectedMidiTarget]);
  const cancelMidiLearn = useCallback(() => {
    engineRef.current?.cancelMidiLearn();
    setMidiLearnTarget(null);
  }, []);
  const setScopeCanvases = useCallback((canvases: ScopeCanvases) => engineRef.current?.setScopeCanvases(canvases), []);
  const signalTone = runtime.engineStatus === "error" || runtime.engineStatus === "panic" ? "red" : runtime.engineStatus === "running" ? "green" : "amber";

  return (
    <div className="app-shell">
      <a className="skip-link" href="#workspace">Skip to instrument</a>
      <header className="topbar">
        <div className="brand"><div className="brand__mark" aria-hidden="true"><span>C</span><span>V</span></div><div><strong>CRTVAR</strong><small>SIGNAL UNDER PRESSURE</small></div></div>
        <button className={`engine-button engine-button--${runtime.engineStatus}`} type="button" onClick={startEngine}>
          <span aria-hidden="true" />{runtime.engineStatus === "running" ? "ENGINE RUNNING" : runtime.engineStatus === "panic" ? "RESUME ENGINE" : "START ENGINE"}
        </button>
        <SelectField label="Input" value={patch.source.kind} onChange={(event) => void changeSource(event.currentTarget.value as SourceKind)}>
          <optgroup label="Generated"><option value="bars">SMPTE color bars</option><option value="grid">Convergence grid</option><option value="scene">Animated test scene</option></optgroup>
          <optgroup label="Local"><option value="image">Open still image…</option><option value="video">Open video…</option><option value="camera">Camera / capture…</option></optgroup>
        </SelectField>
        <SelectField label="Preset" value="current" onChange={(event) => selectPreset(event.currentTarget.value)}>
          <option value="current" disabled>{patch.name}</option>
          <optgroup label="Factory">{FACTORY_PRESETS.map((item, index) => <option key={`factory-${index}`} value={`factory:${index}`}>{item.name}</option>)}</optgroup>
          {userPresets.length > 0 && <optgroup label="This browser">{userPresets.map((item, index) => <option key={`user-${index}`} value={`user:${index}`}>{item.name}</option>)}</optgroup>}
        </SelectField>
        <Toggle pressed={runtime.bypass} onPressedChange={(bypass) => appStore.setRuntime({ bypass })} tone="amber">BYPASS</Toggle>
        <button className={`record-button ${runtime.recording ? "is-recording" : ""}`} type="button" onClick={() => void toggleRecording()} disabled={runtime.engineStatus !== "running" && !runtime.recording}><span aria-hidden="true" />{runtime.recording ? formatClock(runtime.recordingSeconds) : "RECORD"}</button>
        <div className="master-meter"><MasterMeter /></div>
      </header>

      <main id="workspace">
        <section className="stage">
          <div className="monitor-rack">
            <div className="monitor-rack__top"><span>PROGRAM MONITOR</span><span>CVBS // {patch.video.mode === "composite" ? "NTSC-M REF" : "IMAGE FX"}</span></div>
            <div className="monitor">
              <canvas ref={canvasRef} width={640} height={480} aria-label={`Video monitor using ${patch.source.displayName ?? patch.source.kind}; ${patch.video.mode} renderer; ${runtime.bypass ? "bypassed" : "effects active"}.`} />
              <div className="monitor__glass" aria-hidden="true" />
              <div className="monitor__hud"><span>{patch.source.displayName ?? patch.source.kind.toUpperCase()}</span><span><FrameRate /></span></div>
              {runtime.engineStatus === "error" && <div className="monitor__error">VIDEO ENGINE UNAVAILABLE<br /><small>{runtime.engineMessage}</small></div>}
            </div>
            <div className="monitor-rack__footer"><StatusLamp tone={signalTone}>{runtime.engineStatus.toUpperCase()}</StatusLamp><span>{runtime.sourceMessage}</span><span>640 × 480 / 4:3</span></div>
          </div>

          <aside className="macro-rack" aria-label="Performance video controls">
            <header><div><span>VIDEO INTERFERENCE</span><small>PERFORMANCE MACROS</small></div><StatusLamp tone={runtime.compositeLocked ? "green" : "red"}>{runtime.compositeLocked ? "LOCK" : "DRIFT"}</StatusLamp></header>
            <div className="macro-grid">
              {["video.horizontalJitter", "video.verticalRoll", "video.chromaPhase", "video.noise", "video.feedback", "video.burstAmplitude"].map((id) => {
                const descriptor = PARAMETER_BY_ID.get(id as ParameterId)!;
                const key = id.split(".")[1] as keyof CRTVarPatch["video"];
                return <div className="macro-control" key={id}><label><span>{descriptor.shortLabel}</span><output>{typeof patch.video[key] === "number" ? Number(patch.video[key]).toFixed(descriptor.unit === "°" ? 0 : 2) : ""}</output><input aria-label={descriptor.label} type="range" min={descriptor.min} max={descriptor.max} step={descriptor.step} value={Number(patch.video[key])} onChange={(event) => setParameter(id as ParameterId, Number(event.currentTarget.value))} /></label></div>;
              })}
            </div>
            <div className="rack-actions">
              <Toggle pressed={runtime.safeOutput} onPressedChange={(safeOutput) => {
                if (!safeOutput && !window.confirm("Safe Output limits generated flashes and full-field level changes. Disable it for this session?")) return;
                appStore.setRuntime({ safeOutput });
              }} tone="amber">SAFE OUTPUT</Toggle>
              <Toggle pressed={patch.keyboard.capture} onPressedChange={(capture) => appStore.updatePatch((next) => ({ ...next, keyboard: { ...next.keyboard, capture } }))}>COMPUTER KEYS</Toggle>
              <div className="fit-control" role="group" aria-label="Source scaling"><span>SCALING</span><button type="button" aria-pressed={patch.source.fit === "fit"} onClick={() => { engineRef.current?.setFit("fit"); appStore.updatePatch((next) => ({ ...next, source: { ...next.source, fit: "fit" } })); }}>FIT</button><button type="button" aria-pressed={patch.source.fit === "fill"} onClick={() => { engineRef.current?.setFit("fill"); appStore.updatePatch((next) => ({ ...next, source: { ...next.source, fit: "fill" } })); }}>FILL</button></div>
              <button className="midi-button" type="button" onClick={() => void enableMidi()} disabled={!navigator.requestMIDIAccess}>MIDI {runtime.midiStatus.toUpperCase()}</button>
            </div>
          </aside>
        </section>

        <PerformancePads active={runtime.activePads} onPad={handlePad} />
        <Piano octave={patch.keyboard.octave} activeNotes={activeNotes} onNoteOn={noteOn} onNoteOff={noteOff} />

        <nav className="workspace-tabs" aria-label="Instrument workspaces">
          {TABS.map(([id, label, number]) => <button key={id} type="button" aria-current={runtime.activeTab === id ? "page" : undefined} onClick={() => appStore.setTab(id)}><span>{number}</span>{label}</button>)}
        </nav>

        <section className="workspace-panel">
          {runtime.activeTab === "perform" && <div className="perform-workspace">
            <SequencerPanel patch={patch} onPatch={(update) => appStore.updatePatch(update)} />
            <section className="module session-module">
              <header><span>PATCH SESSION</span><small>LOCAL ONLY</small></header>
              <label className="name-field"><span>Patch name</span><input value={patch.name} maxLength={80} onChange={(event) => appStore.updatePatch((next) => ({ ...next, name: event.currentTarget.value }))} /></label>
              <div className="session-actions"><button type="button" onClick={savePreset}>SAVE LOCAL</button><button type="button" onClick={() => downloadBlob(new Blob([serializePatch(patch)], { type: "application/json" }), `${patch.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "crtvar"}.json`)}>EXPORT JSON</button><button type="button" onClick={() => patchInputRef.current?.click()}>IMPORT</button></div>
              {recordingBlob && <button className="recording-ready" type="button" onClick={() => downloadBlob(recordingBlob, `crtvar-${Date.now()}.webm`)}>SAVE RECORDED PERFORMANCE · {(recordingBlob.size / 1024 / 1024).toFixed(1)} MB</button>}
              <p>Media and patches stay in this browser. Local files must be relinked after reload.</p>
              <PresetBrowser factory={FACTORY_PRESETS} users={userPresets} onLoad={loadPreset} onDuplicate={duplicatePreset} onRename={renamePreset} onDelete={deletePreset} />
            </section>
          </div>}
          {runtime.activeTab === "video" && <VideoPanel patch={patch} onParameter={setParameter} onPatch={(update) => appStore.updatePatch(update)} />}
          {runtime.activeTab === "synth" && <SynthPanel patch={patch} onParameter={setParameter} onPatch={(update) => appStore.updatePatch(update)} />}
          {runtime.activeTab === "modulation" && <div className="modulation-workspace"><ModulationPanel patch={patch} onPatch={(update) => appStore.updatePatch(update)} /><section className="module midi-module"><header><span>MIDI ASSIGNMENTS</span><small>NOTE INPUT + 7-BIT CC · NO SYSEX</small></header><div className="midi-learn-row"><SelectField label="Learn target" value={selectedMidiTarget} onChange={(event) => setSelectedMidiTarget(event.currentTarget.value as ParameterId)}>{[...PARAMETER_BY_ID.values()].map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</SelectField><button type="button" disabled={!navigator.requestMIDIAccess} onClick={() => void beginMidiLearn()}>LEARN NEXT CC</button></div>{patch.midiMappings.length === 0 ? <p className="empty-state">No controller assignments in this patch.</p> : <div className="midi-mappings">{patch.midiMappings.map((mapping, index) => <div key={`${mapping.channel}-${mapping.cc}-${mapping.target}`}><span>CH {mapping.channel === "all" ? "ALL" : mapping.channel + 1} · CC {mapping.cc}</span><strong>{PARAMETER_BY_ID.get(mapping.target)?.label}</strong><button type="button" onClick={() => appStore.updatePatch((next) => ({ ...next, midiMappings: next.midiMappings.filter((_, itemIndex) => itemIndex !== index) }))}>REMOVE</button></div>)}</div>}</section></div>}
          {runtime.activeTab === "scopes" && <ScopesWorkspace onCanvases={setScopeCanvases} />}
          {runtime.activeTab === "composite" && <CompositeWorkspace patch={patch} onPatch={(update) => appStore.updatePatch(update)} />}
        </section>
      </main>

      <footer className="footer-strip"><div><StatusLamp tone={signalTone}>{runtime.engineMessage}</StatusLamp><span aria-live="polite">{notice}</span></div><div><span>PATCH {runtime.patchDirty ? "MODIFIED" : "SAVED"}</span><span>SEED 0x{patch.seed.toString(16).padStart(8, "0").toUpperCase()}</span><span>MODEL {patch.engine.modelRevision}</span></div></footer>

      <input ref={fileInputRef} className="sr-only" type="file" onChange={(event) => { const file = event.currentTarget.files?.[0]; if (file) void loadLocalSource(file); event.currentTarget.value = ""; }} />
      <input ref={patchInputRef} className="sr-only" type="file" accept="application/json,.json" onChange={(event) => { const file = event.currentTarget.files?.[0]; if (file) void importPatch(file); event.currentTarget.value = ""; }} />

      {midiLearnTarget && <MidiLearnDialog target={midiLearnTarget} onCancel={cancelMidiLearn} />}
    </div>
  );
}
